import { MultiplayerManager } from '../MultiplayerManager';

// Game configuration interface
interface GameConfig {
    id: string;
    name: string;
    url: string;
    image: string;
    scoringMethod: 'time' | 'game';
    description: string;
}

// Games data interface
interface GamesData {
    games: GameConfig[];
}

export class ThirdPartyGameHelper {
    private static instance: ThirdPartyGameHelper | null = null;
    private scoreInterval: number | null = null;
    private gameWindow: Window | null = null;
    private multiplayerManager: MultiplayerManager;
    private readonly SCORE_INTERVAL = 3000; // 3 seconds for time-based scoring
    private readonly POINTS_PER_INTERVAL = 10; // Points per interval for time-based scoring
    private isScoring = false;
    private currentSessionPoints = 0;
    private gameScore = 0; // Track the actual game score
    private activeIframe: HTMLIFrameElement | null = null;
    private observer: MutationObserver | null = null;
    private lastReportedScore = 0; // Track the last score reported by the game
    private gamesConfig: GamesData | null = null;
    private currentGameConfig: GameConfig | null = null;
    private configLoaded = false;
    private configLoading = false;

    private constructor(multiplayerManager: MultiplayerManager) {
        this.multiplayerManager = multiplayerManager;
        window.addEventListener('blur', () => this.handleWindowBlur());
        window.addEventListener('focus', () => this.handleWindowFocus());
        window.addEventListener('message', this.handleGameMessage.bind(this));
        this.loadGamesConfig();
    }

    public static getInstance(multiplayerManager: MultiplayerManager): ThirdPartyGameHelper {
        if (!ThirdPartyGameHelper.instance) {
            ThirdPartyGameHelper.instance = new ThirdPartyGameHelper(multiplayerManager);
        }
        return ThirdPartyGameHelper.instance;
    }

    private async loadGamesConfig() {
        if (this.configLoaded || this.configLoading) {
            return;
        }

        this.configLoading = true;
        try {
            const response = await fetch('/minigames/games.json');
            if (!response.ok) {
                throw new Error(`Failed to load games config: ${response.status} ${response.statusText}`);
            }
            this.gamesConfig = await response.json();
            this.configLoaded = true;
            console.log('Loaded games configuration:', this.gamesConfig);
        } catch (error) {
            console.error('Error loading games configuration:', error);
            // Fallback to empty config
            this.gamesConfig = { games: [] };
        } finally {
            this.configLoading = false;
        }
    }

    private async ensureConfigLoaded(): Promise<boolean> {
        if (!this.configLoaded && !this.configLoading) {
            await this.loadGamesConfig();
        }
        
        // Wait for config to finish loading if it's currently in progress
        if (this.configLoading) {
            return new Promise<boolean>((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!this.configLoading) {
                        clearInterval(checkInterval);
                        resolve(this.configLoaded);
                    }
                }, 100);
            });
        }
        
        return this.configLoaded;
    }

    private findGameConfig(url: string): GameConfig | null {
        if (!this.gamesConfig || !this.gamesConfig.games || this.gamesConfig.games.length === 0) {
            console.log('No game configs available');
            return null;
        }

        console.log('Finding game config for URL:', url);
        console.log('Available games:', this.gamesConfig.games.map(g => g.url));

        // First try exact URL match
        const exactMatch = this.gamesConfig.games.find(game => game.url === url);
        if (exactMatch) {
            console.log('Found exact match:', exactMatch.id);
            return exactMatch;
        }

        // Then try matching by partial URL (for handling query params, etc.)
        for (const game of this.gamesConfig.games) {
            if (url.includes(game.url) || game.url.includes(url)) {
                console.log('Found partial match:', game.id);
                return game;
            }
        }

        // Look for games by their name in the URL
        for (const game of this.gamesConfig.games) {
            const gameName = game.id.toLowerCase();
            if (url.toLowerCase().includes(gameName)) {
                console.log('Found match by game name in URL:', game.id);
                return game;
            }
        }

        // Fall back to extracting the game ID from the URL path
        try {
            const urlObj = new URL(url, window.location.origin);
            const pathParts = urlObj.pathname.split('/');
            const filename = pathParts[pathParts.length - 1];
            const gameId = filename.split('.')[0]; // Extract the name part before extension
            
            console.log('Extracted game ID from URL:', gameId);
            const matchByExtracted = this.gamesConfig.games.find(game => game.id === gameId);
            
            if (matchByExtracted) {
                console.log('Found match by extracted ID:', matchByExtracted.id);
                return matchByExtracted;
            }
            
            // Try case-insensitive check for common games
            const lowerGameId = gameId.toLowerCase();
            const commonGame = this.gamesConfig.games.find(game => 
                game.id.toLowerCase() === lowerGameId || 
                game.name.toLowerCase() === lowerGameId
            );
            
            if (commonGame) {
                console.log('Found common game match:', commonGame.id);
                return commonGame;
            }
            
            console.log('No matching game config found for:', url);
            return null;
        } catch (e) {
            console.error('Error parsing URL to find game config:', e);
            return null;
        }
    }

    private handleGameMessage(event: MessageEvent) {
        // Check if the message is from a game
        if (event.data && event.data.type === 'minigame_score') {
            console.log('Received score from game:', event.data.score, 'from game:', event.data.game, 'final:', event.data.final);
            console.log('Current game config:', this.currentGameConfig);
            
            // Only process game-based scoring if the current game is configured for it
            if (this.currentGameConfig && this.currentGameConfig.scoringMethod === 'game') {
                console.log('Processing game-based score for:', this.currentGameConfig.name);
                
                // Update the game score
                const previousScore = this.gameScore;
                this.gameScore = event.data.score;
                
                // Calculate the score difference to add
                const scoreToAdd = this.gameScore - this.lastReportedScore;
                console.log('Previous reported score:', this.lastReportedScore, 'New score:', this.gameScore, 'Adding points:', scoreToAdd);
                this.lastReportedScore = this.gameScore;
                
                if (scoreToAdd > 0) {
                    // Add the score difference to the player's score
                    console.log('Adding points to player:', scoreToAdd);
                    this.multiplayerManager.addPoints(scoreToAdd);
                    this.currentSessionPoints += scoreToAdd;
                    console.log(`Added ${scoreToAdd} points from game score, total: ${this.currentSessionPoints}`);
                }
                
                // If this is the final score, stop scoring
                if (event.data.final) {
                    console.log('Game ended with final score:', this.gameScore);
                    this.stopScoring();
                }
            } else {
                console.log('Not processing score - either no current game config or not game-based scoring');
                if (this.currentGameConfig) {
                    console.log('Current scoring method:', this.currentGameConfig.scoringMethod);
                }
            }
        }
    }

    public async handleGameLink(url: string) {
        console.log('Opening game in new window:', url);
        
        // Ensure config is loaded
        await this.ensureConfigLoaded();
        
        // Find the game config for this URL
        this.currentGameConfig = this.findGameConfig(url);
        console.log('Game config for URL:', url, this.currentGameConfig);
        
        this.gameWindow = window.open(url, '_blank');
        if (this.gameWindow) {
            // Reset score tracking
            this.gameScore = 0;
            this.lastReportedScore = 0;
            this.currentSessionPoints = 0;
            
            // Start scoring when window is opened
            this.startScoring();

            // Check if window is closed every second
            const checkWindow = setInterval(() => {
                if (this.gameWindow?.closed) {
                    this.stopScoring();
                    clearInterval(checkWindow);
                    this.gameWindow = null;
                }
            }, 1000);
        }
    }

    public async handleGameIframe(iframe: HTMLIFrameElement) {
        console.log('Game opened in iframe');
        
        // Stop any previous scoring if active
        this.stopScoring();
        
        // Clear any previous observer
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        // Store the active iframe reference
        this.activeIframe = iframe;
        
        // Get the URL from the iframe
        const url = iframe.src;
        console.log('Iframe URL:', url);
        
        // Ensure config is loaded
        await this.ensureConfigLoaded();
        
        // Find the game config for this URL
        this.currentGameConfig = this.findGameConfig(url);
        console.log('Game config for iframe:', url, this.currentGameConfig);
        
        // Reset score tracking
        this.gameScore = 0;
        this.lastReportedScore = 0;
        this.currentSessionPoints = 0;
        
        // Start scoring when iframe is loaded
        this.startScoring();

        // Create a MutationObserver to watch for iframe removal
        this.setupIframeObserver(iframe);
        
        // Add a failsafe - check if iframe is still in DOM every few seconds
        this.setupIframeExistenceCheck(iframe);
    }
    
    private setupIframeObserver(iframe: HTMLIFrameElement) {
        // Create and store the MutationObserver
        this.observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const removedNode of mutation.removedNodes) {
                    // Check if the iframe itself was removed
                    if (removedNode === iframe) {
                        console.log('Iframe removed from DOM - stopping scoring');
                        this.stopScoring();
                        this.observer?.disconnect();
                        this.observer = null;
                        this.activeIframe = null;
                        return;
                    }
                    
                    // Check if any ancestor of the iframe was removed
                    if (removedNode.contains && removedNode.contains(iframe)) {
                        console.log('Iframe parent removed from DOM - stopping scoring');
                        this.stopScoring();
                        this.observer?.disconnect();
                        this.observer = null;
                        this.activeIframe = null;
                        return;
                    }
                }
            }
        });

        // Start observing the document body for all changes that might affect the iframe
        this.observer.observe(document.body, { 
            childList: true,
            subtree: true
        });
    }
    
    private setupIframeExistenceCheck(iframe: HTMLIFrameElement) {
        // Check every 5 seconds if the iframe is still in the DOM
        const existenceCheck = window.setInterval(() => {
            // If scoring is active but iframe is no longer in DOM, stop scoring
            if (this.isScoring && this.activeIframe === iframe) {
                if (!document.body.contains(iframe)) {
                    console.log('Failsafe: Iframe no longer in DOM - stopping scoring');
                    this.stopScoring();
                    clearInterval(existenceCheck);
                    
                    if (this.observer) {
                        this.observer.disconnect();
                        this.observer = null;
                    }
                    
                    this.activeIframe = null;
                }
            } else {
                // Clear the check if we're no longer scoring or have a different iframe
                clearInterval(existenceCheck);
            }
        }, 5000);
    }

    private handleWindowBlur() {
        // When user switches away from our window, they might be playing the game
        if (this.gameWindow && !this.gameWindow.closed) {
            this.startScoring();
        }
    }

    private handleWindowFocus() {
        // When user comes back to our window, stop scoring
        if (!this.activeIframe) {
            this.stopScoring();
        }
    }

    private startScoring() {
        if (this.isScoring) {
            return; // Already scoring
        }
        console.log('Starting scoring for game:', this.currentGameConfig?.id);
        this.isScoring = true;
        this.multiplayerManager.startThirdPartyGameScoring();
        
        // Only set up time-based scoring if:
        // 1. The game is configured for time-based scoring, OR
        // 2. No game config is found (fallback to time-based for unknown games)
        const useTimeBased = !this.currentGameConfig || this.currentGameConfig.scoringMethod === 'time';
        
        if (useTimeBased) {
            console.log('Using time-based scoring for this game');
            this.scoreInterval = window.setInterval(() => {
                console.log('Adding time-based points');
                this.currentSessionPoints += this.POINTS_PER_INTERVAL;
                this.multiplayerManager.addPoints(this.POINTS_PER_INTERVAL);
            }, this.SCORE_INTERVAL);
        } else {
            // For game-based scoring, we still set up a failsafe interval,
            // but it only adds points if the game hasn't reported any score yet
            console.log('Using game-based scoring for this game');
            this.scoreInterval = window.setInterval(() => {
                // Only add fallback points if we haven't received any score updates
                // and we've been waiting for at least 10 seconds (3 intervals)
                if (this.lastReportedScore === 0 && this.currentSessionPoints === 0) {
                    console.log('No score reported from game after extended period, adding minimal fallback points');
                    this.currentSessionPoints += this.POINTS_PER_INTERVAL / 5; // Reduced fallback points
                    this.multiplayerManager.addPoints(this.POINTS_PER_INTERVAL / 5);
                }
            }, this.SCORE_INTERVAL * 3); // Check less frequently for game-based scoring
        }
    }

    private stopScoring() {
        if (!this.isScoring) {
            return; // Not scoring
        }
        console.log('Stopping scoring, earned points this session: ' + this.currentSessionPoints);
        this.isScoring = false;
        if (this.scoreInterval) {
            clearInterval(this.scoreInterval);
            this.scoreInterval = null;
        }
        
        // Ensure final score update with accumulated points
        this.multiplayerManager.updateTotalScore(this.currentSessionPoints);
        
        // Reset score tracking
        this.gameScore = 0;
        this.lastReportedScore = 0;
        this.currentGameConfig = null;
        
        // Then stop scoring system but keep UI visible
        this.multiplayerManager.stopThirdPartyGameScoring();
    }

    public cleanup() {
        this.stopScoring();
        
        // Clean up game window if open
        if (this.gameWindow && !this.gameWindow.closed) {
            this.gameWindow.close();
        }
        this.gameWindow = null;
        
        // Clean up observer if active
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        
        this.activeIframe = null;
    }
} 